/**
 * 助力运营看板（C2 验收页）。
 *
 * Phase 4-2 / Stream 4 / Plan §12 / Design §5.7.
 *
 * 4 Tab 布局：
 *  1. 看板：顶部漏斗（4 KPI Statistic）+ inviter 排行榜（Table，count 倒序）
 *  2. 风控：风控告警（24h 高速激活）+ 超额票审计（reached_15_no_voucher / voucher_not_used_30d）
 *  3. 反查：按 unionId 反查 boost_relationships（客服回答工单用）
 *  4. 冻结：手动冻结表单（userId + reason）+ 当前冻结列表（24h 内可撤销）
 *
 * 权限：
 *  - boost:read              所有 Tab 可见，但具体内容按子权限再细分
 *  - boost:read_relationships 反查 Tab 内容可见
 *  - boost:audit             风控/超额票 Tab 内容可见
 *  - boost:freeze            冻结表单可见
 *  - freeze:revert           撤销按钮可见
 *
 * 错误处理：后端 51215 / 51104 / 13000 / 51301-51304 均含友好 message，
 *           onError 中 e.response.data.message 直接展示。
 */
import { useMemo, useState } from 'react';
import {
  Card, Tabs, Statistic, Row, Col, Table, Form, Input, Button, Space,
  Tag, Modal, message, Popconfirm, Alert, Empty, Select, Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  getFunnel, getInviterRanking, getRiskAlerts, getOversupplyAudit,
  findRelationshipsByUnionId, listFreezes, freezeBoost, revertFreeze,
  type InviterRankingRow, type RiskAlertRow, type OversupplyAuditRow,
  type BoostRelationshipRow, type BoostFreezeRow, type BoostFreezeStatus,
} from '../../api/boost';

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN');
}

function canRevertNow(freeze: BoostFreezeRow): boolean {
  return freeze.status === 'active' && Date.now() < freeze.canRevertUntil;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 1：看板
// ─────────────────────────────────────────────────────────────────────────────
function DashboardTab() {
  const funnelQuery = useQuery({ queryKey: ['boost', 'funnel'], queryFn: getFunnel });
  const rankingQuery = useQuery({
    queryKey: ['boost', 'inviter-ranking'],
    queryFn: () => getInviterRanking(50),
  });

  const f = funnelQuery.data;

  const columns: ColumnsType<InviterRankingRow> = [
    { title: '排名', key: 'rank', width: 60, render: (_, __, idx) => idx + 1 },
    { title: 'inviter userId', dataIndex: 'userId', key: 'userId', width: 220, ellipsis: true },
    {
      title: '累计计票', dataIndex: 'count', key: 'count', width: 100,
      render: (v: number) => <Tag color={v >= 15 ? 'green' : 'blue'}>{v}/15</Tag>,
    },
    {
      title: '已发券', dataIndex: 'voucherIssued', key: 'voucherIssued', width: 100,
      render: (v: boolean, row) => v ? (
        <Tag color="green">{formatTs(row.voucherIssuedAt)}</Tag>
      ) : <Tag>未发</Tag>,
    },
    { title: 'lifecycle', dataIndex: 'lifecycle', key: 'lifecycle', width: 100 },
    { title: '最近更新', dataIndex: 'updatedAt', key: 'updatedAt', width: 170, render: formatTs },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="顶部漏斗（实时）" loading={funnelQuery.isLoading}>
        <Row gutter={16}>
          <Col span={6}><Statistic title="累计绑定" value={f?.totalBindings ?? 0} /></Col>
          <Col span={6}><Statistic title="已激活" value={f?.totalActivated ?? 0} /></Col>
          <Col span={6}><Statistic title="已发券（15 票达成）" value={f?.totalIssuedVouchers ?? 0} /></Col>
          <Col span={6}><Statistic title="已核销" value={f?.totalUsedVouchers ?? 0} /></Col>
        </Row>
      </Card>
      <Card size="small" title="inviter 排行榜（前 50）">
        <Table<InviterRankingRow>
          rowKey="userId"
          loading={rankingQuery.isLoading}
          dataSource={rankingQuery.data?.items ?? []}
          columns={columns}
          pagination={false}
          data-testid="boost-ranking-table"
        />
      </Card>
    </Space>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 2：风控
// ─────────────────────────────────────────────────────────────────────────────
function RiskTab() {
  const [windowHours, setWindowHours] = useState(24);
  const [threshold, setThreshold] = useState(5);

  const alertsQuery = useQuery({
    queryKey: ['boost', 'risk-alerts', windowHours, threshold],
    queryFn: () => getRiskAlerts(windowHours, threshold),
  });
  const auditQuery = useQuery({
    queryKey: ['boost', 'oversupply-audit'],
    queryFn: getOversupplyAudit,
  });

  const alertColumns: ColumnsType<RiskAlertRow> = [
    { title: 'inviter userId', dataIndex: 'inviterUserId', key: 'inviterUserId', width: 220 },
    {
      title: `${windowHours}h 内激活`, dataIndex: 'activated24h', key: 'a24h', width: 130,
      render: (v: number) => <Tag color="red">{v}</Tag>,
    },
    { title: '累计计票', dataIndex: 'totalCount', key: 'totalCount', width: 100 },
    { title: '最近激活', dataIndex: 'lastActivatedAt', key: 'lastAt', width: 170, render: formatTs },
  ];

  const auditColumns: ColumnsType<OversupplyAuditRow> = [
    { title: 'userId', dataIndex: 'userId', key: 'userId', width: 220 },
    {
      title: '累计计票', dataIndex: 'count', key: 'count', width: 100,
      render: (v: number) => <Tag color={v >= 15 ? 'orange' : 'blue'}>{v}</Tag>,
    },
    {
      title: '已发券', dataIndex: 'voucherIssued', key: 'voucherIssued', width: 100,
      render: (v: boolean) => v ? <Tag color="green">是</Tag> : <Tag color="red">否</Tag>,
    },
    {
      title: '异常类型', dataIndex: 'anomaly', key: 'anomaly', width: 220,
      render: (v: OversupplyAuditRow['anomaly']) => (
        <Tag color="orange">
          {v === 'reached_15_no_voucher' ? '已达 15 但未发券' : '发券 30+ 天未使用'}
        </Tag>
      ),
    },
    { title: '最近更新', dataIndex: 'updatedAt', key: 'updatedAt', width: 170, render: formatTs },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        size="small"
        title="风控告警（高速激活）"
        extra={
          <Space>
            <span>窗口（小时）：</span>
            <Select value={windowHours} onChange={setWindowHours} style={{ width: 90 }}
              options={[{ value: 1, label: '1h' }, { value: 6, label: '6h' }, { value: 12, label: '12h' }, { value: 24, label: '24h' }, { value: 72, label: '72h' }]}
              data-testid="risk-window-select"
            />
            <span>阈值：</span>
            <Select value={threshold} onChange={setThreshold} style={{ width: 80 }}
              options={[{ value: 3, label: '≥3' }, { value: 5, label: '≥5' }, { value: 10, label: '≥10' }, { value: 20, label: '≥20' }]}
            />
          </Space>
        }
      >
        <Alert
          type="info" showIcon
          message={`${windowHours}h 内激活 ≥ ${threshold} 个 boost relationships 的 inviter`}
          description="可能是黑产批量扫码；点击 userId 可跳冻结 Tab 处置"
          style={{ marginBottom: 12 }}
        />
        <Table<RiskAlertRow>
          rowKey="inviterUserId"
          loading={alertsQuery.isLoading}
          dataSource={alertsQuery.data?.items ?? []}
          columns={alertColumns}
          pagination={false}
          data-testid="risk-alerts-table"
        />
      </Card>
      <Card size="small" title="超额票审计">
        <Table<OversupplyAuditRow>
          rowKey={(r) => `${r.userId}-${r.anomaly}`}
          loading={auditQuery.isLoading}
          dataSource={auditQuery.data?.items ?? []}
          columns={auditColumns}
          pagination={false}
        />
      </Card>
    </Space>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 3：反查
// ─────────────────────────────────────────────────────────────────────────────
function LookupTab() {
  const [form] = Form.useForm<{ unionId: string }>();
  const [submitted, setSubmitted] = useState<string>('');

  const query = useQuery({
    queryKey: ['boost', 'relationships', submitted],
    queryFn: () => findRelationshipsByUnionId(submitted),
    enabled: submitted !== '',
  });

  const columns: ColumnsType<BoostRelationshipRow> = [
    { title: 'relationshipId', dataIndex: 'relationshipId', key: 'rid', width: 200 },
    { title: 'inviter userId', dataIndex: 'inviterUserId', key: 'iuid', width: 200 },
    { title: 'workId', dataIndex: 'workId', key: 'wid', width: 160 },
    { title: 'boundAt', dataIndex: 'boundAt', key: 'ba', width: 170, render: formatTs },
    {
      title: '已激活', dataIndex: 'activated', key: 'act', width: 90,
      render: (v: boolean) => v ? <Tag color="green">是</Tag> : <Tag>否</Tag>,
    },
    { title: 'activatedAt', dataIndex: 'activatedAt', key: 'aat', width: 170, render: formatTs },
    { title: 'activatedUserId', dataIndex: 'activatedUserId', key: 'auid', width: 200 },
    {
      title: 'superseded', dataIndex: 'superseded', key: 'sup', width: 100,
      render: (v: boolean) => v ? <Tag color="orange">是</Tag> : <Tag>否</Tag>,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="按 unionId 反查 boost_relationships（客服工单用）">
        <Form
          form={form}
          layout="inline"
          data-testid="lookup-form"
          onFinish={(v) => setSubmitted(v.unionId.trim())}
        >
          <Form.Item
            name="unionId" label="unionId"
            rules={[{ required: true, message: '请填 unionId' }]}
          >
            <Input style={{ width: 380 }} placeholder="例 union-xxxx" />
          </Form.Item>
          <Button type="primary" htmlType="submit" data-testid="lookup-submit">查询</Button>
        </Form>
      </Card>
      {submitted && (
        <Card size="small" title={`查询结果（unionId = ${submitted}）`}>
          {!query.isLoading && (query.data?.items ?? []).length === 0 ? (
            <Empty description="该 unionId 无 boost_relationships 记录（未扫码 / 未绑定）" />
          ) : (
            <Table<BoostRelationshipRow>
              rowKey="relationshipId"
              loading={query.isLoading}
              dataSource={query.data?.items ?? []}
              columns={columns}
              pagination={false}
              data-testid="lookup-result-table"
            />
          )}
        </Card>
      )}
    </Space>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 4：冻结管理
// ─────────────────────────────────────────────────────────────────────────────
function FreezeTab() {
  const qc = useQueryClient();
  const canRevert = usePermission(PERMISSIONS.FREEZE_REVERT);
  const [freezeForm] = Form.useForm<{ userId: string; reason: string }>();
  const [statusFilter, setStatusFilter] = useState<BoostFreezeStatus | 'all'>('active');
  const [revertTarget, setRevertTarget] = useState<BoostFreezeRow | null>(null);
  const [revertForm] = Form.useForm<{ reason: string }>();

  const listQuery = useQuery({
    queryKey: ['boost', 'freezes', statusFilter],
    queryFn: () => listFreezes(statusFilter === 'all' ? {} : { status: statusFilter }),
  });

  const errorHandler = (label: string) =>
    (e: { response?: { data?: { message?: string } }; message?: string }) =>
      message.error(e.response?.data?.message ?? e.message ?? label);

  const freezeMutation = useMutation({
    mutationFn: (vars: { userId: string; reason: string }) =>
      freezeBoost(vars.userId, vars.reason),
    onSuccess: (data) => {
      message.success(`已冻结 ${data.userId}，24h 内可撤销（截至 ${formatTs(data.canRevertUntil)}）`);
      freezeForm.resetFields();
      qc.invalidateQueries({ queryKey: ['boost'] });
    },
    onError: errorHandler('冻结失败'),
  });

  const revertMutation = useMutation({
    mutationFn: (vars: { freezeId: string; reason: string }) =>
      revertFreeze(vars.freezeId, vars.reason),
    onSuccess: () => {
      message.success('已撤销冻结');
      setRevertTarget(null);
      revertForm.resetFields();
      qc.invalidateQueries({ queryKey: ['boost'] });
    },
    onError: errorHandler('撤销失败'),
  });

  function submitFreeze() {
    freezeForm.validateFields().then((v) => {
      freezeMutation.mutate({ userId: v.userId.trim(), reason: v.reason.trim() });
    });
  }

  function submitRevert() {
    if (!revertTarget) return;
    revertForm.validateFields().then((v) => {
      revertMutation.mutate({ freezeId: revertTarget.freezeId, reason: v.reason.trim() });
    });
  }

  const columns: ColumnsType<BoostFreezeRow> = [
    { title: 'freezeId', dataIndex: 'freezeId', key: 'fid', width: 200 },
    { title: 'inviter userId', dataIndex: 'userId', key: 'uid', width: 180 },
    { title: 'frozenBy', dataIndex: 'frozenBy', key: 'fb', width: 130 },
    { title: 'frozenAt', dataIndex: 'frozenAt', key: 'fa', width: 170, render: formatTs },
    { title: 'reason', dataIndex: 'reason', key: 'reason', width: 240, ellipsis: true },
    {
      title: 'status', dataIndex: 'status', key: 'status', width: 100,
      render: (s: BoostFreezeStatus) => {
        const color = s === 'active' ? 'red' : s === 'reverted' ? 'green' : 'default';
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: '24h 窗口', dataIndex: 'canRevertUntil', key: 'rw', width: 170,
      render: (until: number, row) => {
        if (row.status !== 'active') return '-';
        const ms = until - Date.now();
        if (ms <= 0) return <Tag color="default">已过</Tag>;
        const h = Math.floor(ms / 3600000);
        const m = Math.floor((ms % 3600000) / 60000);
        return <Tag color="orange">{h}h{m}m</Tag>;
      },
    },
    {
      title: '操作', key: 'actions', width: 120,
      render: (_: unknown, row) => (
        canRevert && canRevertNow(row) ? (
          <Tooltip title="仅 24h 内可撤销；超过后只能等过期 sweep">
            <Button size="small" danger
              onClick={() => {
                setRevertTarget(row);
                revertForm.resetFields();
              }}
              data-testid={`freeze-revert-${row.freezeId}`}
            >
              撤销
            </Button>
          </Tooltip>
        ) : null
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <PermissionGate point={PERMISSIONS.BOOST_FREEZE}>
        <Card size="small" title="手动冻结 inviter">
          <Alert
            type="warning" showIcon
            message="冻结后该 inviter 不再计票 / 发券，24h 内超管可撤销；超过 24h 仅能等待后台 sweep 自动过期"
            style={{ marginBottom: 12 }}
          />
          <Form form={freezeForm} layout="vertical">
            <Form.Item
              label="inviter userId" name="userId"
              rules={[{ required: true, message: '请填 userId' }]}
            >
              <Input placeholder="例 u-xxxx" />
            </Form.Item>
            <Form.Item
              label="冻结原因（5..200 字符，会写入审计 + 通知用户）"
              name="reason"
              rules={[
                { required: true, message: '请填原因' },
                { min: 5, max: 200, message: '5..200 字符' },
              ]}
            >
              <Input.TextArea rows={3} maxLength={200} showCount />
            </Form.Item>
            <Button
              type="primary" danger
              loading={freezeMutation.isPending}
              onClick={submitFreeze}
              data-testid="freeze-submit"
            >
              确认冻结
            </Button>
          </Form>
        </Card>
      </PermissionGate>

      <Card
        size="small"
        title="冻结历史"
        extra={
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
            options={[
              { value: 'active', label: '生效中' },
              { value: 'reverted', label: '已撤销' },
              { value: 'expired', label: '已过期' },
              { value: 'all', label: '全部' },
            ]}
            data-testid="freeze-status-filter"
          />
        }
      >
        <Table<BoostFreezeRow>
          rowKey="freezeId"
          loading={listQuery.isLoading}
          dataSource={listQuery.data?.items ?? []}
          columns={columns}
          pagination={false}
          data-testid="freezes-table"
        />
      </Card>

      <Modal
        title={`撤销冻结 ${revertTarget?.freezeId ?? ''}`}
        open={revertTarget !== null}
        onCancel={() => { setRevertTarget(null); revertForm.resetFields(); }}
        onOk={submitRevert}
        confirmLoading={revertMutation.isPending}
        destroyOnClose
        data-testid="revert-modal"
      >
        <Alert
          type="info" showIcon
          message={revertTarget ? `inviter: ${revertTarget.userId}` : ''}
          description={`冻结理由：${revertTarget?.reason ?? ''}`}
          style={{ marginBottom: 16 }}
        />
        <Form form={revertForm} layout="vertical" preserve={false}>
          <Form.Item
            label="撤销原因（5..200 字符）"
            name="reason"
            rules={[
              { required: true, message: '请填撤销原因' },
              { min: 5, max: 200, message: '5..200 字符' },
            ]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 主页面
// ─────────────────────────────────────────────────────────────────────────────
type TabKey = 'dashboard' | 'risk' | 'lookup' | 'freeze';

function BoostPageInner() {
  const [tab, setTab] = useState<TabKey>('dashboard');
  const canReadAudit = usePermission(PERMISSIONS.BOOST_AUDIT);
  const canReadRelationships = usePermission(PERMISSIONS.BOOST_READ_RELATIONSHIPS);

  const items = useMemo(() => {
    const list: { key: TabKey; label: string; children: React.ReactNode }[] = [];
    list.push({ key: 'dashboard', label: '看板', children: <DashboardTab /> });
    if (canReadAudit) list.push({ key: 'risk', label: '风控', children: <RiskTab /> });
    if (canReadRelationships) list.push({ key: 'lookup', label: '反查', children: <LookupTab /> });
    list.push({ key: 'freeze', label: '冻结管理', children: <FreezeTab /> });
    return list;
  }, [canReadAudit, canReadRelationships]);

  return (
    <Card title="助力运营看板（C2）" data-testid="boost-page">
      <Tabs activeKey={tab} onChange={(k) => setTab(k as TabKey)} items={items} />
    </Card>
  );
}

export default function BoostPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.BOOST_READ}
      fallback={
        <Card>
          <Alert
            type="error" showIcon message="无访问权限"
            description="助力运营看板（C2）需要 boost:read 权限，请联系超管。"
          />
        </Card>
      }
    >
      <BoostPageInner />
    </PermissionGate>
  );
}
